import { useMemo } from 'react'
import { useWatch, type Control } from 'react-hook-form'

import { isFieldRequired, isFieldVisible } from '../lib/formHelper'
import type { DynamicField } from '../types'

/**
 * Processes an array of DynamicField definitions against current form values and
 * returns the subset of fields that are visible, with `required` resolved.
 *
 * Usage:
 *  const visibleFields = useDynamicFields(control, fields)
 *
 *  - `control` is the react-hook-form `control` instance
 *  - `fields` is an array of DynamicField coming from the backend
 *
 * Example:
 *  const fields = [{ param_key: 'email', required: true }, { param_key: 'role', visible_if: { is_admin: true } }]
 *  const visibleFields = useDynamicFields(control, fields)
 *
 * Returns:
 *  Array<DynamicField & { required: boolean }>
 */
export function useDynamicFields<T extends {}>(control: Control<T>, fields?: Array<DynamicField>) {
  const values = useWatch({ control }) as Record<string, unknown>

  const processed = useMemo(
    () =>
      fields
        ?.filter((field) => isFieldVisible(field, values))
        .map((field) => ({ ...field, required: isFieldRequired(field, values) })),
    [fields, values],
  )

  return processed
}
